import axios from "axios";
import {AuthState, RootState} from "../../interfaces/auth-state";
import { ActionTree, GetterTree, MutationTree } from "vuex";

const LOGOUT_WARNING_THRESHOLD = 250; // number of seconds before displaying the logout warning

const loadToken = () => {
  const token = sessionStorage.getItem("access_token") || "";

  if (token) {
    axios.defaults.headers.common.Authorization = token;
  }

  return token;
};

export const authState: AuthState = {
  access_token: loadToken(),
  refresh_token: sessionStorage.getItem("refresh_token") || "",
  expires_at: Number(sessionStorage.getItem("token_expires_at")) || 0,
  status: "",
  hasLoadedOnce: false,
  errorMessage: "",
  showSessionWarning: false,
  tokenExpired: false,
  now: Math.floor(Date.now() / 1000),
  sessionTimerStarted: false,
  profile: JSON.parse(sessionStorage.getItem("user_profile") || "{}")
};

export const authGetters: GetterTree<AuthState, RootState> = {
  isAuthenticated: (state: AuthState) => !!state.access_token,
  authStatus: (state: AuthState) => state.status,
  authErrorMessage: (state: AuthState) => state.errorMessage,
  allowedAccess: (state: AuthState) => state.profile.allowed_navigation
};

export const authActions: ActionTree<AuthState, any> = {
  userRequest: ({ commit }) => {
    commit("userRequest");

    return axios.get("/users/me")
      .then(resp => {
        commit("userSuccess", resp.data.user);
      })
      .catch(err => {
        commit("userError", err);
      });
  },

  loginRequest: ({ commit, dispatch }, payload) => {
    commit("loginRequest");

    const data = {
      email: payload.email,
      password: payload.password
    };

    return axios.post("/auth/login", data)
      .then(async resp => {
        commit("loginSuccess", resp.data);
        await dispatch("userRequest");
      }).catch((err) => {
        commit("loginError", err);
      });
  },

  logout: ({ commit, dispatch }) => {
    commit("logout");
  },

  extendUserSession: ({ commit, state }) => {
    commit("loginRequest");

    return axios.post("/auth/extend-session", {
          refresh_token: state.refresh_token
    }).then(resp => {
      commit("loginSuccess", resp.data);
    }).catch(err => {
      commit("loginError", err);
    });
  },

  startSessionTimer: ({ commit, dispatch, state }) => {
    commit("checkSessionExpiration");

    if (!state.sessionTimerStarted) {
      commit("sessionTimerStarted");

      setInterval(() => {
        commit("updateCurrentTimestamp");

        if (state.now > state.expires_at) {
          // token is expired, log user out and redirect them to the login screen
          dispatch("logout");
          return;
        }

        commit("checkSessionExpiration", dispatch);
      }, 1000 * 60); // run the session update
    }
  }
};

export const authMutations: MutationTree<AuthState> = {
  loginRequest: (state: AuthState) => {
    state.status = "loading";
  },

  loginSuccess: (state: AuthState, response) => {
    const access_token = "Bearer " + response.access_token;
    const now = Math.floor(Date.now() / 1000);

    sessionStorage.setItem("access_token", access_token);
    sessionStorage.setItem("refresh_token", response.refresh_token);
    sessionStorage.setItem("token_expires_at", response.expires_in + now);

    axios.defaults.headers.common.Authorization = access_token;

    state.status = "auth success";
    state.access_token = access_token;
    state.refresh_token = response.refresh_token;
    state.expires_at = response.expires_in + now;
    state.hasLoadedOnce = true;
    state.showSessionWarning = false;
  },

  ssoLogin: (state: AuthState, response) => {
    const access_token = response.accessToken;
    const now = Math.floor(Date.now() / 1000);

    sessionStorage.setItem("access_token", access_token);
    sessionStorage.setItem("refresh_token", response.refreshToken);
    sessionStorage.setItem("token_expires_at", response.expires + now);

    axios.defaults.headers.common.Authorization = access_token;

    state.status = "auth success";
    state.access_token = access_token;
    state.refresh_token = response.refreshToken;
    state.expires_at = response.expires + now;
    state.hasLoadedOnce = true;
    state.showSessionWarning = false;
  },

  loginError: (state: AuthState, err) => {
    let errorMessage = "";

    switch (err.response.status) {
      case 401:
        errorMessage = "The credentials provided were incorrect";
        break;
      case 422:
        errorMessage = "There was a problem with the credentials provided.";
        break;
      default:
        errorMessage = "An error occurred during login, please contact support";
        break;
    }

    state.status = "error";
    state.hasLoadedOnce = true;
    state.errorMessage = errorMessage;
  },

  logout: (state: AuthState) => {
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("token_expires_at");
    sessionStorage.removeItem("user_profile");
    axios.defaults.headers.common.Authorization = null;
    state.status = "logged out";
    state.access_token = null;
    state.refresh_token = null;
    state.expires_at = 0;
  },

  sessionTimerStarted: (state: AuthState) => {
    state.sessionTimerStarted = true;
  },

  checkSessionExpiration: (state: AuthState) => {
    const expires_at = Number(state.expires_at);

    if (!!state.access_token && (expires_at < (state.now + LOGOUT_WARNING_THRESHOLD))) {
      state.showSessionWarning = true;
      return;
    }

    state.showSessionWarning = false;
  },

  updateCurrentTimestamp: (state: AuthState) => {
    state.now = Math.floor(Date.now() / 1000);
  },

  userRequest: (state: AuthState) => {
    state.status = "loading";
  },

  userSuccess: (state: AuthState, userData) => {
    sessionStorage.setItem("user_profile", JSON.stringify(userData));
    state.status = "success";
    state.profile = userData;
  }
};

export default {
  name: "auth",
  namespaced: true,
  state: authState,
  getters: authGetters,
  actions: authActions,
  mutations: authMutations
};
